import React, {
	useState,
	useEffect,
	useCallback,
	forwardRef,
	useImperativeHandle,
} from "react";

import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Switch from "@material-ui/core/Switch";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import TableRowSkeleton from "../../../components/TableRowSkeleton";
import ConfirmationModal from "../../../components/ConfirmationModal";
import RequestTypeModal from "../../../components/RequestTypeModal";

// Mesma causa-raiz documentada em VisaoGeralPanel/index.js: MainContainer (do
// proprio Whaticket) da ao filho direto height:100%+overflowY:hidden, entao
// este painel precisa ser um unico elemento flex:1 com overflow proprio.
//
// O botao "+ Novo Tipo" NAO mora mais aqui - fica na mesma linha das abas
// (MinhasSolicitacoes/index.js), para nao criar espaco vertical entre a
// linha de abas e o cabecalho da tabela. Este painel continua dono do
// estado do modal (precisa dele tambem para o clique de Editar por linha),
// entao expoe "openCreate" via ref para o botao do pai poder acionar.
const useStyles = makeStyles(theme => ({
	root: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		overflowX: "auto",
		...(theme.scrollbarStyles || {}),
	},
	loadingRoot: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(6),
	},
	centeredState: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: theme.spacing(6, 2),
		color: theme.palette.text.secondary,
	},
	statusChip: {
		fontWeight: 600,
	},
	conexoesTodas: {
		color: theme.palette.text.secondary,
		fontStyle: "italic",
	},
}));

const CatalogoTiposPanel = forwardRef((props, ref) => {
	const classes = useStyles();
	const [types, setTypes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingType, setEditingType] = useState(null);
	const [confirmTarget, setConfirmTarget] = useState(null);
	const [togglingId, setTogglingId] = useState(null);
	// Conexoes da empresa, so para traduzir whatsapp_id -> nome na tabela.
	// A API de Solicitacoes guarda apenas o id (a tabela "whatsapps" vive no
	// banco do Whaticket), entao o nome e resolvido aqui na apresentacao.
	const [connections, setConnections] = useState([]);

	const nomesDasConexoes = useCallback(
		ids => {
			if (!ids || ids.length === 0) return "";
			return ids
				.map(id => {
					const conexao = connections.find(c => c.id === id);
					// Sem FK entre bancos, uma conexao excluida no Whaticket deixa
					// o vinculo orfao. Mostrar "#id" e melhor que sumir em silencio.
					return conexao ? conexao.name : `#${id}`;
				})
				.join(", ");
		},
		[connections]
	);

	useEffect(() => {
		const fetchConnections = async () => {
			try {
				const { data } = await api.get("/whatsapp");
				setConnections(Array.isArray(data) ? data : []);
			} catch (err) {
				// Falhar aqui nao pode derrubar o catalogo: sem os nomes a
				// coluna cai para "#id", que ainda e utilizavel.
				setConnections([]);
			}
		};
		fetchConnections();
	}, []);

	const fetchTypes = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/solicitacoes/tipos/todos");
			setTypes(data);
			setError(null);
		} catch (err) {
			setError(err);
			toastError(err);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchTypes();
	}, [fetchTypes]);

	const handleNew = () => {
		setEditingType(null);
		setModalOpen(true);
	};

	// O botao "+ Novo Tipo" mora no pai (mesma linha das abas) - expoe
	// handleNew aqui para o botao de la conseguir abrir este modal.
	useImperativeHandle(ref, () => ({
		openCreate: handleNew,
	}));

	const handleEdit = type => {
		setEditingType(type);
		setModalOpen(true);
	};

	const handleModalSaved = () => {
		setModalOpen(false);
		fetchTypes();
	};

	// Toggle direto na tabela: unico lugar que muda "active" fora do fluxo de
	// exclusao - evita dois caminhos de UI diferentes para o mesmo estado.
	const handleToggleActive = async type => {
		setTogglingId(type.id);
		try {
			await api.patch(`/solicitacoes/tipos/${type.id}`, {
				active: !type.active,
			});
			toast.success(
				i18n.t(
					!type.active
						? "minhasSolicitacoes.catalogo.toasts.toggleActiveSuccess"
						: "minhasSolicitacoes.catalogo.toasts.toggleInactiveSuccess"
				)
			);
			fetchTypes();
		} catch (err) {
			toastError(err);
		} finally {
			setTogglingId(null);
		}
	};

	const handleConfirmDelete = async () => {
		const target = confirmTarget;
		setConfirmTarget(null);
		try {
			const { data } = await api.delete(`/solicitacoes/tipos/${target.id}`);
			toast.success(
				i18n.t(
					data.mode === "hard"
						? "minhasSolicitacoes.catalogo.toasts.deleteHardSuccess"
						: "minhasSolicitacoes.catalogo.toasts.deleteSoftSuccess"
				)
			);
			fetchTypes();
		} catch (err) {
			toastError(err);
		}
	};

	if (loading && types.length === 0) {
		return (
			<div className={classes.loadingRoot}>
				<CircularProgress />
			</div>
		);
	}

	if (error && types.length === 0) {
		return (
			<div className={classes.centeredState}>
				<ErrorOutlineIcon fontSize="large" />
				<Typography variant="body1" style={{ marginTop: 8 }}>
					{i18n.t("minhasSolicitacoes.catalogo.errorState")}
				</Typography>
				<Button
					variant="outlined"
					color="primary"
					onClick={fetchTypes}
					style={{ marginTop: 12 }}
				>
					{i18n.t("minhasSolicitacoes.catalogo.retry")}
				</Button>
			</div>
		);
	}

	return (
		<Paper id="catalogo-tipos-panel" className={classes.root} variant="outlined">
			<RequestTypeModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				editingType={editingType}
				onSaved={handleModalSaved}
			/>

			<ConfirmationModal
				title={i18n.t("minhasSolicitacoes.catalogo.confirmDelete.title")}
				open={!!confirmTarget}
				onClose={() => setConfirmTarget(null)}
				onConfirm={handleConfirmDelete}
			>
				{i18n.t("minhasSolicitacoes.catalogo.confirmDelete.body")}
			</ConfirmationModal>

			<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="left">
								{i18n.t("minhasSolicitacoes.catalogo.columns.name")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("minhasSolicitacoes.catalogo.columns.sla")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("minhasSolicitacoes.catalogo.columns.connections")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("minhasSolicitacoes.catalogo.columns.status")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("minhasSolicitacoes.catalogo.columns.actions")}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{!loading && types.length === 0 && (
							<TableRow>
								<TableCell colSpan={5}>
									<div id="catalogo-tipos-empty" className={classes.centeredState}>
										<Typography variant="body1">
											{i18n.t("minhasSolicitacoes.catalogo.emptyState")}
										</Typography>
									</div>
								</TableCell>
							</TableRow>
						)}
						{types.map(type => (
							<TableRow key={type.id} id={`catalogo-tipos-item-${type.id}`}>
								<TableCell align="left">{type.name}</TableCell>
								<TableCell align="center">{type.sla_hours}</TableCell>
								{/* Lista vazia so acontece no catalogo BASE do produto
								    (company_id NULL) - demandas criadas pelo painel exigem
								    ao menos uma conexao. Mostrar "Todas" em vez de celula
								    em branco: em branco o admin nao distingue "vale para
								    todas" de "ainda nao configurei". */}
								<TableCell align="center">
									{!type.whatsapp_ids || type.whatsapp_ids.length === 0 ? (
										<span className={classes.conexoesTodas}>
											{i18n.t("minhasSolicitacoes.catalogo.connectionsAll")}
										</span>
									) : (
										<span title={nomesDasConexoes(type.whatsapp_ids)}>
											{nomesDasConexoes(type.whatsapp_ids)}
										</span>
									)}
								</TableCell>
								<TableCell align="center">
									<Tooltip
										title={i18n.t(
											type.active
												? "minhasSolicitacoes.catalogo.status.active"
												: "minhasSolicitacoes.catalogo.status.inactive"
										)}
									>
										<span>
											<Switch
												size="small"
												color="primary"
												checked={type.active}
												disabled={togglingId === type.id}
												onChange={() => handleToggleActive(type)}
											/>
										</span>
									</Tooltip>
									<Chip
										size="small"
										className={classes.statusChip}
										label={i18n.t(
											type.active
												? "minhasSolicitacoes.catalogo.status.active"
												: "minhasSolicitacoes.catalogo.status.inactive"
										)}
									/>
								</TableCell>
								<TableCell align="center">
									<Tooltip title={i18n.t("minhasSolicitacoes.catalogo.actions.edit")}>
										<IconButton size="small" onClick={() => handleEdit(type)}>
											<EditIcon fontSize="small" />
										</IconButton>
									</Tooltip>
									<Tooltip title={i18n.t("minhasSolicitacoes.catalogo.actions.delete")}>
										<IconButton size="small" onClick={() => setConfirmTarget(type)}>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</TableCell>
							</TableRow>
						))}
						{loading && types.length > 0 && <TableRowSkeleton columns={4} />}
					</TableBody>
				</Table>
		</Paper>
	);
});

export default CatalogoTiposPanel;
